"use client";

import { Trans } from "@lingui/react/macro";
import { type FragmentOf, readFragment } from "gql.tada";
import { ChartLineIcon, DatabaseIcon, SettingsIcon } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { ADS_POLICIES } from "@/lib/authorization/policies/ads-analytics";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import { cn } from "@/lib/utils";
import { useViewOrder } from "../../../_contexts/view-order-context";
import { updateViewOrder } from "../../../ads/[projectId]/(top)/board/action";
import { useUpdateViewOrder } from "../../sortable/sortable-hooks";
import SortableSidebarGroup from "../../sortable/sortable-sidebar-group";
import {
  type ADSSidebarItem,
  SPECIAL_VIEW_IDS,
} from "../../sortable/sortable-types";
import { isPathActive, reorderViewsByIds } from "../../sortable/sortable-utils";
import { AdsAnalyticsProjectItemFragment } from "./fragments";

interface SortableProjectViewsProps {
  project: FragmentOf<typeof AdsAnalyticsProjectItemFragment>;
  workspaceId: string;
  lang: string;
}

export function AdsSidebarSortable({
  project: projectFragment,
  workspaceId,
  lang,
}: SortableProjectViewsProps) {
  const project = readFragment(
    AdsAnalyticsProjectItemFragment,
    projectFragment,
  );
  const adsAnalytics = project.adsAnalytics;
  const checkPolicy = useCheckPolicy();
  const canEditViews = checkPolicy(ADS_POLICIES["ads-view:edit"]).ok;

  const { getViewOrder } = useViewOrder();
  const handleUpdateViewOrder = useUpdateViewOrder(updateViewOrder);
  const storedOrderIds = getViewOrder(project.id);

  const orderedViews = useMemo(
    () => reorderViewsByIds(adsAnalytics?.views || [], storedOrderIds),
    [storedOrderIds, adsAnalytics?.views],
  );

  const needsDataSetup = canEditViews && !adsAnalytics?.latestDataDate;

  const views: ADSSidebarItem[] = useMemo(() => {
    if (!adsAnalytics) {
      // 未初期化時は Setup 行（AdsSetupDataSourcesItem）に集約し、Settings は出さない
      return [];
    }

    const latestDataDate = adsAnalytics.latestDataDate ?? "";
    const result: ADSSidebarItem[] = [];

    if (latestDataDate) {
      result.push({
        id: SPECIAL_VIEW_IDS.ALL,
        name: "All",
        workspaceId,
        projectId: project.id,
        projectType: "ads",
        latestDataDate,
        isAllCard: true,
        sortable: false,
      });
    }

    result.push(
      ...orderedViews.map((view) => ({
        id: view.id,
        name: view.name,
        workspaceId,
        projectId: project.id,
        projectType: "ads" as const,
        latestDataDate,
        sortable: canEditViews,
      })),
    );

    // データ未接続時は Setup 行に集約し、Settings と二重表示しない
    if (canEditViews && latestDataDate) {
      result.push({
        id: SPECIAL_VIEW_IDS.SETTINGS,
        name: "Settings",
        workspaceId,
        projectId: project.id,
        projectType: "ads",
        latestDataDate: "",
        sortable: false,
      });
    }

    return result;
  }, [orderedViews, workspaceId, project.id, adsAnalytics, canEditViews]);

  const generateViewHref = (item: ADSSidebarItem): string => {
    const base = `/${encodeURIComponent(lang)}/${encodeURIComponent(workspaceId)}/ads/${encodeURIComponent(project.id)}`;

    if (item.isAllCard) {
      return `${base}/default`;
    }

    if (item.id === SPECIAL_VIEW_IDS.SETTINGS) {
      return `${base}/settings`;
    }

    return `${base}/${encodeURIComponent(item.id)}`;
  };

  const renderViewContent = (item: ADSSidebarItem): React.ReactNode => {
    if (item.isAllCard) {
      return (
        <>
          <ChartLineIcon className="w-4 h-4" />
          <Trans id="default-view.name">All</Trans>
        </>
      );
    }

    if (item.id === SPECIAL_VIEW_IDS.SETTINGS) {
      return (
        <>
          <SettingsIcon className="w-4 h-4" />
          <Trans>Settings</Trans>
        </>
      );
    }

    return (
      <>
        <ChartLineIcon className="w-4 h-4 shrink-0" />
        <div className="line-clamp-2 min-w-0" title={item.name}>
          {item.name}
        </div>
      </>
    );
  };

  return (
    <>
      {needsDataSetup ? (
        <AdsSetupDataSourcesItem
          lang={lang}
          workspaceId={workspaceId}
          projectId={project.id}
        />
      ) : null}
      <SortableSidebarGroup
        views={views}
        updateViewOrder={handleUpdateViewOrder}
        projectId={project.id}
        projectType="ads"
        actionId={adsAnalytics?.id || "settings-only"}
        className="h-auto min-h-7"
        generateViewHref={generateViewHref}
        renderViewContent={renderViewContent}
        isReorderEnabled={canEditViews}
      />
    </>
  );
}

function AdsSetupDataSourcesItem({
  lang,
  workspaceId,
  projectId,
}: {
  lang: string;
  workspaceId: string;
  projectId: string;
}) {
  const pathname = usePathname();
  const href = `/${encodeURIComponent(lang)}/${encodeURIComponent(workspaceId)}/ads/${encodeURIComponent(projectId)}/settings`;
  const isActive = isPathActive(pathname, href);

  return (
    <SidebarMenuSub>
      <SidebarMenuSubItem
        className="w-full"
        style={{ width: "calc(100% + 8px)" }}
      >
        <SidebarMenuSubButton
          asChild
          isActive={isActive}
          className={cn(
            "w-full min-w-0",
            isActive && "bg-primary/10! text-primary! [&_svg]:text-primary!",
          )}
        >
          <Link href={href}>
            <DatabaseIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">
              <Trans>Connect data sources</Trans>
            </span>
            <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5">
              <Trans>Setup</Trans>
            </Badge>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    </SidebarMenuSub>
  );
}
